// ── Tramos horarios que asigna el ADMIN ("Asignar evento") ────────────────
// Dos de los tipos del grid de fichaje manual NO crean un evento suelto: crean
// un PAR inicio/fin acotado a un tramo del MISMO día.
//
//   · 'compensacion' → COMPENSATION_START/END (8/9). Horas compensadas: se
//     pagan con la bolsa del trabajador.
//   · 'ausencia'     → ABSENCE_START/END (10/11). Ausencia por horas: SIEMPRE
//     se recupera (decisión de producto 2026-09-22). No reduce la jornada
//     esperada, no cuenta como trabajada y no genera offset de bolsa: el
//     trabajador simplemente DEBE esas horas. Ver src/lib/hourBalance.js
//     (makeBalanceDayResolver) — ahí NO hay nada que tocar.
//
// Módulo PURO (sin React, sin imports): testeado en
// src/utils/__tests__/assignHourRange.test.js.
// Gemelo en la app: TemControlApp/src/lib/assignHourRange.js — si se cambia
// una regla aquí, cambiarla allí (no está bajo el guard de check:shared
// porque cada repo lo coloca en su carpeta de lógica pura).

export const HOUR_RANGE_KINDS = {
  COMPENSATION: 'compensacion',
  ABSENCE: 'ausencia',
};

// 'YYYY-MM-DD' → 'DD/MM/YYYY' sin pasar por Date (nada de desfases de zona).
const fmtDayKey = (key) => {
  const m = String(key || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(key || '');
};

// Validación del tramo: ambas horas, fin POSTERIOR al inicio (mismo día) y fin
// ANTERIOR a `maxEndHm` (23:59), que está reservado a los días completos —
// isFullDayCompensationRange lee un par que acaba ahí como día libre entero, y
// una ausencia de día entero tiene su propio flujo ("Día completo").
// 'HH:mm' se compara como string sin problema.
// Devuelve el mensaje de error en castellano, o null si el tramo es válido.
export const hourRangeError = ({ kind, start, end, maxEndHm = '23:59' } = {}) => {
  const isAbsence = kind === HOUR_RANGE_KINDS.ABSENCE;
  if (!start || !end) {
    return isAbsence
      ? 'Indica la hora de inicio y la de fin de la ausencia.'
      : 'Indica la hora de inicio y la de fin de las horas compensadas.';
  }
  if (end <= start) return 'La hora de fin debe ser posterior a la de inicio.';
  if (end >= maxEndHm) {
    return isAbsence
      ? `Una ausencia por horas debe terminar antes de las ${maxEndHm}. Para un día entero usa "Día completo".`
      : `Un tramo compensado debe terminar antes de las ${maxEndHm}. Para un día libre entero usa "Asignar compensación".`;
  }
  return null;
};

// ── Rechazo del backend por hora de entrada (enforceStartHour) ─────────────
// LIMITACIÓN CONOCIDA del backend (Temcontrol-back, routes/pikachuRouter.js):
// POST /api/event aplica enforceStartHour a TODOS los tipos salvo el 2 y el 4
// (vacaciones y día completo). Los tipos 10/11 NO están exentos, así que una
// ausencia por horas que empiece ANTES de la hora de entrada del trabajador se
// rechaza con 400 "Cannot add event before person's start hour with
// enforceStartHour = TRUE".
//
// NO se arregla desde el cliente ni se disimula moviendo la hora: la hora
// pedida ES el dato. Lo único que hacemos es que el admin entienda qué ha
// pasado y qué puede hacer.
//
// Se busca en el texto del error Y en el cuerpo crudo de la respuesta porque
// services/api.js (ambos repos) ya traduce ese 400 al castellano antes de
// llegar aquí: el mensaje en inglés puede no estar en `message`.
export const isStartHourRejection = (err) => {
  if (!err) return false;
  const data = err.data;
  const hay = [
    typeof err === 'string' ? err : err.message,
    err.bodySnippet,
    data && typeof data === 'object' ? data.message : data,
  ].filter(Boolean).join(' | ').toLowerCase();
  return hay.includes("before person's start hour")
    || hay.includes('before person’s start hour') // apóstrofo tipográfico
    || hay.includes('enforcestarthour')
    || hay.includes('control de hora de entrada'); // ya traducido por api.js
};

// Mensaje para el ADMIN cuando el backend tumba el fichaje.
// Dice las tres cosas que necesita saber: que NO se ha creado, por qué, y que
// el horario con el que compara el servidor puede estar desfasado.
// `what` = qué se intentaba crear. Por defecto la ausencia por horas, que es
// donde nació el mensaje; el fichaje manual suelto y el tramo compensado
// pasan el suyo (el backend solo exime a los tipos 2 y 4).
export const startHourRejectionMessage = ({
  workerName, dayKey, start, end, what = 'la ausencia por horas',
} = {}) => {
  const who = workerName ? ` de ${workerName}` : '';
  const when = dayKey ? ` del ${fmtDayKey(dayKey)}` : '';
  const hours = start && end ? ` (${start}–${end})` : (start ? ` (${start})` : '');
  return `No se ha podido crear ${what}${who}${when}${hours}: el servidor RECHAZA `
    + 'cualquier fichaje anterior a la hora de entrada del trabajador, y esta persona tiene ACTIVADO '
    + 'el control de hora de entrada en su ficha. Desactívaselo (o ajusta su jornada) y vuelve a '
    + 'intentarlo; no cambies la hora, porque la hora pedida es el dato. '
    + 'Aviso: el servidor puede estar comparando con una versión ANTIGUA de su horario, así que el '
    + 'rechazo puede saltar incluso con una hora que parezca válida con el horario vigente hoy.';
};

// Envuelve el error del backend para que la UI muestre el texto de arriba.
// Si el fallo NO es el de enforceStartHour, se devuelve tal cual (cada pantalla
// ya sabe mostrar un ApiError). Una sesión caducada nunca se reescribe.
export const startHourRejectionError = (err, ctx) => {
  if (err && err.reason === 'session-ended') return err;
  if (!isStartHourRejection(err)) return err;
  const wrapped = new Error(startHourRejectionMessage(ctx));
  wrapped.status = (err && err.status) != null ? err.status : null;
  wrapped.reason = 'enforce-start-hour';
  return wrapped;
};
